import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../SsnCardVerification.module.scss";
import { SSN_CARD_CAPTURE_FEEDBACK, type TerminalSsnCardErrorCode } from "../../../../shared/types/ssnCardVerification";
import { usePageView } from "../../../../shared/analytics/usePageView";
import { trackClicked } from "../../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../../shared/analytics/documentCaptureEvents";
import { trackSsnCardCaptureError } from "../../../../shared/utils/sentryTracking";
import { CSS_GUIDE_DURATION_MS, CSS_GUIDE_REVEAL_DELAY_MS, guideAssetUrl, nextGuideNonce } from "./captureGuide";

/** Retakes offered per side when image QA fails. After these, the last shot is used as-is. */
const MAX_RETAKES_PER_SIDE = 2;

// Lazy so the Regula WASM wrapper never enters the parent UMD bundle — it loads
// only when the capture screen actually mounts.
const RegulaCapture = lazy(() =>
  import("../../regula/manual/RegulaCapture").then((m) => ({ default: m.RegulaCapture })),
);

interface CaptureScreenProps {
  /** Clean capture — hand both cropped images (raw base64) to the hook to POST. */
  onSubmit: (frontBase64: string, backBase64: string) => void;
  /**
   * Terminal failure — propagated to the parent. `feedback` carries the specific
   * capture reason behind a generic code (e.g. ssn_card_verification_failed).
   */
  onError: (code: TerminalSsnCardErrorCode, message: string, feedback?: string) => void;
}

/**
 * CaptureScreen — the full-screen capture surface (state `capturing`). Sequences two
 * shutter-gated captures through one Regula reader: front, then back. The heading is
 * the only thing that changes between them — there is no interstitial.
 *
 * A failed image-quality check is not terminal: the member is offered up to
 * MAX_RETAKES_PER_SIDE retakes of that side, after which the last shot is used as-is
 * so a struggling member still completes. Device/permission failures stay terminal.
 *
 * - both sides captured     → onSubmit(front, back) (parent flips to `processing`)
 * - device/reader failure   → onError(code, message, feedback) to the parent
 * - user closed the reader  → onError("user_canceled") (mirrors legal-id's Veriff cancel)
 */
export function CaptureScreen({ onSubmit, onError }: CaptureScreenProps) {
  const { t } = useTranslation();
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });

  const [side, setSide] = useState<"front" | "back">("front");
  const [captureKey, setCaptureKey] = useState(0);
  const [showRetakeNotice, setShowRetakeNotice] = useState(false);
  // Transient card guide (VEX-7717), one showing per capture. `done` is a dead
  // end by design: Regula rebuilds its whole camera layout whenever the stream
  // restarts, and any rebuild while the guide is still addressable restarts its
  // animation — so the guide is handed out exactly once and then retired, and
  // a camera switch retires it immediately rather than letting it replay.
  const [guideStage, setGuideStage] = useState<"idle" | "pending" | "visible" | "done">("idle");
  const [guideSrc, setGuideSrc] = useState<string | null>(null);
  const retakesRef = useRef(0);
  const frontBase64Ref = useRef<string | null>(null);

  // The guide's lifetime is owned here, not by a lead-in/out inside the CSS
  // keyframes: the pseudo-element must not exist before reveal, because its
  // background SVG starts animating the moment it does and `opacity: 0` will
  // not pause that (see CSS_GUIDE_REVEAL_DELAY_MS). The artwork URL is minted
  // at reveal so each showing gets its own animation timeline.
  useEffect(() => {
    if (guideStage === "pending") {
      const timer = window.setTimeout(() => {
        setGuideSrc(guideAssetUrl(side, nextGuideNonce()));
        setGuideStage("visible");
      }, CSS_GUIDE_REVEAL_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
    if (guideStage === "visible") {
      const timer = window.setTimeout(() => setGuideStage("done"), CSS_GUIDE_DURATION_MS);
      return () => window.clearTimeout(timer);
    }
    return undefined;
    // `side` is read only when the pending timer fires, and it can only change
    // via nextCapture(), which resets the stage to "idle" first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideStage]);

  /** Start the next capture in the same reader (retake, or the other side). */
  const nextCapture = () => {
    setGuideStage("idle");
    setGuideSrc(null);
    setCaptureKey((k) => k + 1);
  };

  const handleCapture = (image: { base64: string }, qaFailed: boolean) => {
    if (qaFailed && retakesRef.current < MAX_RETAKES_PER_SIDE) {
      retakesRef.current += 1;
      setShowRetakeNotice(true);
      nextCapture();
      return;
    }

    // Accepted — either it passed QA, or the retakes are spent and we go with it.
    if (qaFailed) {
      trackSsnCardCaptureError("ssn_card_verification_failed", {
        feedback: SSN_CARD_CAPTURE_FEEDBACK.QUALITY_FAILED,
        phase: "accepted_after_retakes",
        side,
      });
    }
    retakesRef.current = 0;
    setShowRetakeNotice(false);

    if (side === "front") {
      frontBase64Ref.current = image.base64;
      setSide("back");
      nextCapture();
      return;
    }

    // Both sides in hand — the hook flips to `processing`, unmounting this screen.
    onSubmit(frontBase64Ref.current ?? "", image.base64);
  };

  // User closed the reader — terminal, propagated to the parent (mirrors legal-id's
  // Veriff "user_canceled"). The parent/IVA decides whether to relaunch.
  const handleCancel = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.CLOSE_DOCUMENT_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE, targetAction: "navigate" });
    onError("user_canceled", t("ssnCard.error.canceled"));
  };

  return (
    <div className={styles.captureScreen}>
      <h2 className={styles.captureHeading}>
        {t(side === "front" ? "ssnCard.capturing.headingFront" : "ssnCard.capturing.headingBack")}
      </h2>
      {showRetakeNotice && (
        <div className={styles.captureRetakeBanner} role="status">
          <img
            className={styles.captureRetakeBannerIcon}
            src="/assets/svg/icon-alert-circle.svg"
            alt=""
            aria-hidden="true"
          />
          {t("ssnCard.capturing.retake")}
        </div>
      )}
      <div id="ssn-card-capture-root" className={styles.captureReader}>
        <Suspense
          fallback={
            <div className={styles.spinner} role="status" aria-label={t("ssnCard.capturing.subtitle")} />
          }
        >
          <RegulaCapture
            captureKey={captureKey}
            onCapture={handleCapture}
            onError={onError}
            onCancel={handleCancel}
            // Only the first camera start of a capture arms the guide; the
            // repeats that follow a camera switch must not restart it.
            onCameraStarted={() => setGuideStage((stage) => (stage === "idle" ? "pending" : stage))}
            onCameraSwitched={() => setGuideStage("done")}
            side={side}
            guideSide={guideStage === "visible" ? side : undefined}
            guideSrc={guideStage === "visible" ? guideSrc ?? undefined : undefined}
          />
        </Suspense>
      </div>
    </div>
  );
}
